import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  EnvelopeIcon,
  PhoneIcon,
  MapPinIcon,
  CalendarIcon,
  IdentificationIcon,
  ArrowLeftIcon,
  DocumentTextIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  UserIcon,
  StarIcon,
} from '@heroicons/react/24/outline';
import { format } from 'date-fns';
import { getFullProfilePictureUrl } from '../../utils/imageUtils';
import { User } from '../../types/user';
import UserDetailsContent from '../../components/admin/UserDetailsContent';
import { interviewAPI } from '../../services/api';
import { Interview, InterviewStatus } from '../../types/interview';

interface Application {
  id: number;
  jobId: number;
  jobTitle: string;
  status: string;
  createdAt: string;
  updatedAt: string;
  resumeUrl?: string;
  coverLetterUrl?: string;
  currentCompany?: string;
  currentPosition?: string;
  expectedSalary?: number;
  experienceYears?: number;
}

interface UserProfileData extends User {
  applications?: Application[];
  createdAt?: string;
}

const AdminUserProfilePage: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const { token, user: currentUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [applicationsLoading, setApplicationsLoading] = useState(false);
  const [userData, setUserData] = useState<UserProfileData | null>(null);
  const [applications, setApplications] = useState<Application[]>([]);
  const [activeTab, setActiveTab] = useState<'profile' | 'applications' | 'details'>('profile');
  const [error, setError] = useState<string | null>(null);
  const [expandedApplicationId, setExpandedApplicationId] = useState<number | null>(null);
  const [interviewsMap, setInterviewsMap] = useState<Record<number, Interview[]>>({});
  const [interviewsLoading, setInterviewsLoading] = useState<Record<number, boolean>>({});

  const fetchUserProfile = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch(`/api/users/${userId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setUserData(data);
      } else {
        setError('Failed to load user profile');
      }
    } catch (error) {
      console.error('Error fetching user profile:', error);
      setError('Network error when loading user profile');
    } finally {
      setLoading(false);
    }
  }, [userId, token]);

  const fetchUserApplications = useCallback(async () => {
    if (!userId || !token) return;
    
    try {
      setApplicationsLoading(true);
      
      const response = await fetch(`/api/applications/user/${userId}?size=50`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setApplications(data.content || []);
      } else {
        console.error('Failed to load user applications');
      }
    } catch (error) {
      console.error('Error fetching user applications:', error);
    } finally {
      setApplicationsLoading(false);
    }
  }, [userId, token]);

  useEffect(() => {
    if (userId && token) {
      fetchUserProfile();
    }
  }, [fetchUserProfile]);

  useEffect(() => {
    if (userId && token) {
      fetchUserApplications();
    }
  }, [userId, token, fetchUserApplications]);

  useEffect(() => {
    if (activeTab === 'applications' && applications.length === 0) {
      fetchUserApplications();
    }
  }, [activeTab, applications.length, fetchUserApplications]);

  const fetchInterviewsForApplication = useCallback(async (applicationId: number) => {
    if (interviewsMap[applicationId] || interviewsLoading[applicationId]) return;

    try {
      setInterviewsLoading(prev => ({ ...prev, [applicationId]: true }));
      const response = await interviewAPI.getByApplicationId(applicationId);
      setInterviewsMap(prev => ({ ...prev, [applicationId]: response.data }));
    } catch (error) {
      console.error('Error fetching interviews for application:', error);
      setInterviewsMap(prev => ({ ...prev, [applicationId]: [] }));
    } finally {
      setInterviewsLoading(prev => ({ ...prev, [applicationId]: false }));
    }
  }, [interviewsMap, interviewsLoading]);

  const toggleApplicationExpand = (applicationId: number) => {
    if (expandedApplicationId === applicationId) {
      setExpandedApplicationId(null);
    } else {
      setExpandedApplicationId(applicationId);
      fetchInterviewsForApplication(applicationId);
    }
  };

  const getInterviewStatusColor = (status: InterviewStatus) => {
    switch (status) {
      case InterviewStatus.ASSIGNED:
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300';
      case InterviewStatus.IN_PROGRESS:
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300';
      case InterviewStatus.COMPLETED:
        return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
    }
  };

  const getRatingColor = (rating: number) => {
    if (rating >= 70) return 'text-green-600 dark:text-green-400';
    if (rating >= 50) return 'text-yellow-600 dark:text-yellow-400';
    return 'text-red-600 dark:text-red-400';
  };

  const getRatingBarColor = (rating: number) => {
    if (rating >= 70) return 'bg-green-500';
    if (rating >= 50) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  const getOverallScore = (responses: { rating: number }[]) => {
    if (!responses || responses.length === 0) return 0;
    const sum = responses.reduce((acc, r) => acc + r.rating, 0);
    return Math.round(sum / responses.length);
  };

  const getStatusIcon = (status: string) => {
    switch (status.toLowerCase()) {
      case 'submitted':
        return <ClockIcon className="h-4 w-4 text-blue-500" />;
      case 'under_review':
        return <ExclamationTriangleIcon className="h-4 w-4 text-yellow-500" />;
      case 'accepted':
        return <CheckCircleIcon className="h-4 w-4 text-green-500" />;
      case 'rejected':
        return <XCircleIcon className="h-4 w-4 text-red-500" />;
      default:
        return <ClockIcon className="h-4 w-4 text-gray-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'submitted':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300';
      case 'under_review':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300';
      case 'accepted':
        return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300';
      case 'rejected':
        return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
    }
  };

  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), 'MMM dd, yyyy');
    } catch (error) {
      return 'Invalid date';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading user profile...</p>
        </div>
      </div>
    );
  }

  if (error || !userData) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <XCircleIcon className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400">{error || 'User not found'}</p>
          <button
            onClick={() => navigate('/admin/users')}
            className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
          >
            Back to Users
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/admin/users')}
            className="flex items-center text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 mb-4"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            Back to Users
          </button>
          
          <div className="flex items-center space-x-4">
            {userData.profilePictureUrl ? (
              <img
                src={getFullProfilePictureUrl(userData.profilePictureUrl)}
                alt={`${userData.firstName} ${userData.lastName}`}
                className="h-20 w-20 rounded-full object-cover ring-4 ring-offset-4 dark:ring-offset-gray-800 ring-indigo-500 dark:ring-indigo-400"
              />
            ) : (
              <div className="h-20 w-20 rounded-full bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center text-indigo-800 dark:text-indigo-300 text-2xl font-medium ring-4 ring-offset-4 dark:ring-offset-gray-800 ring-indigo-500 dark:ring-indigo-400">
                {userData.firstName?.[0]}{userData.lastName?.[0]}
              </div>
            )}
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
                {userData.firstName} {userData.lastName}
              </h1>
              <p className="text-lg text-gray-600 dark:text-gray-400">{userData.email}</p>
              <div className="flex items-center mt-2">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  userData.isActive ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                }`}>
                  {userData.isActive ? 'Active' : 'Inactive'}
                </span>
                {userData.mfaEnabled && (
                  <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
                    2FA Enabled
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 dark:border-gray-700 mb-8">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('profile')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'profile'
                  ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
              }`}
            >
              Profile Information
            </button>
            <button
              onClick={() => setActiveTab('details')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'details'
                  ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
              }`}
            >
              User Details
            </button>
            <button
              onClick={() => setActiveTab('applications')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'applications'
                  ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
              }`}
            >
              Applications ({applications.length})
            </button>
          </nav>
        </div>

        {/* Tab Content */}
        {activeTab === 'profile' && (
          <div className="space-y-6">
            {/* Basic Information */}
            <div className="bg-white dark:bg-gray-800 shadow-lg dark:shadow-[0_10px_15px_-3px_rgba(0,0,0,0.3),0_4px_6px_-2px_rgba(0,0,0,0.2)] rounded-lg border border-gray-200/50 dark:border-gray-700/50">
              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100">Basic Information</h2>
              </div>
              <div className="px-6 py-4">
                <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
                  <div>
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Full Name</dt>
                    <dd className="mt-1 text-sm text-gray-900 dark:text-gray-100">
                      {userData.firstName} {userData.lastName}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Email Address</dt>
                    <dd className="mt-1 text-sm text-gray-900 dark:text-gray-100 flex items-center">
                      <EnvelopeIcon className="h-4 w-4 mr-2 text-gray-400" />
                      {userData.email}
                    </dd>
                  </div>
                  {userData.phoneNumber && (
                    <div>
                      <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Phone Number</dt>
                      <dd className="mt-1 text-sm text-gray-900 dark:text-gray-100 flex items-center">
                        <PhoneIcon className="h-4 w-4 mr-2 text-gray-400" />
                        {userData.phoneNumber}
                      </dd>
                    </div>
                  )}
                  {(userData.city || userData.country) && (
                    <div>
                      <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Location</dt>
                      <dd className="mt-1 text-sm text-gray-900 dark:text-gray-100 flex items-center">
                        <MapPinIcon className="h-4 w-4 mr-2 text-gray-400" />
                        {[userData.city, userData.country].filter(Boolean).join(', ')}
                      </dd>
                    </div>
                  )}
                  <div>
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Account Status</dt>
                    <dd className="mt-1">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        userData.isActive ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                      }`}>
                        {userData.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Two-Factor Authentication</dt>
                    <dd className="mt-1">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        userData.mfaEnabled ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300' : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                      }`}>
                        {userData.mfaEnabled ? 'Enabled' : 'Disabled'}
                      </span>
                    </dd>
                  </div>
                </dl>
              </div>
            </div>


            {/* Account Information */}
            <div className="bg-white dark:bg-gray-800 shadow-lg dark:shadow-[0_10px_15px_-3px_rgba(0,0,0,0.3),0_4px_6px_-2px_rgba(0,0,0,0.2)] rounded-lg border border-gray-200/50 dark:border-gray-700/50">
              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100">Account Information</h2>
              </div>
              <div className="px-6 py-4">
                <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
                  <div>
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">User ID</dt>
                    <dd className="mt-1 text-sm text-gray-900 dark:text-gray-100 flex items-center">
                      <IdentificationIcon className="h-4 w-4 mr-2 text-gray-400" />
                      {userData.id}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Role</dt>
                    <dd className="mt-1 text-sm text-gray-900 dark:text-gray-100">
                      {userData.role}
                    </dd>
                  </div>
                  {userData.createdAt && (
                    <div>
                      <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Member Since</dt>
                      <dd className="mt-1 text-sm text-gray-900 dark:text-gray-100 flex items-center">
                        <CalendarIcon className="h-4 w-4 mr-2 text-gray-400" />
                        {formatDate(userData.createdAt)}
                      </dd>
                    </div>
                  )}
                  {userData.lastLogin && (
                    <div>
                      <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Last Login</dt>
                      <dd className="mt-1 text-sm text-gray-900 dark:text-gray-100 flex items-center">
                        <CalendarIcon className="h-4 w-4 mr-2 text-gray-400" />
                        {userData.lastLogin instanceof Date ? formatDate(userData.lastLogin.toISOString()) : formatDate(String(userData.lastLogin))}
                      </dd>
                    </div>
                  )}
                </dl>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'applications' && (
          <div className="bg-white dark:bg-gray-800 shadow-lg dark:shadow-[0_10px_15px_-3px_rgba(0,0,0,0.3),0_4px_6px_-2px_rgba(0,0,0,0.2)] rounded-lg border border-gray-200/50 dark:border-gray-700/50">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100">Job Applications</h2>
            </div>
            <div className="px-6 py-4">
              {applicationsLoading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
                  <p className="mt-2 text-gray-600 dark:text-gray-400">Loading applications...</p>
                </div>
              ) : applications.length === 0 ? (
                <div className="text-center py-8">
                  <DocumentTextIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600 dark:text-gray-400">No applications found</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-700/50">
                      <tr>
                        <th className="w-10 px-3 py-3"></th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Job Title
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Applied Date
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Last Updated
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Experience
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Expected Salary
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                      {applications.map((application) => (
                        <React.Fragment key={application.id}>
                          <tr
                            onClick={() => toggleApplicationExpand(application.id)}
                            className="hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer"
                          >
                            <td className="px-3 py-4">
                              {expandedApplicationId === application.id ? (
                                <ChevronDownIcon className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                              ) : (
                                <ChevronRightIcon className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                {application.jobTitle}
                              </div>
                              <div className="text-sm text-gray-500 dark:text-gray-400">
                                Job ID: {application.jobId}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(application.status)}`}>
                                {getStatusIcon(application.status)}
                                <span className="ml-1 capitalize">{application.status.replace('_', ' ')}</span>
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                              {formatDate(application.createdAt)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                              {formatDate(application.updatedAt)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                              {application.experienceYears ? `${application.experienceYears} years` : 'Not specified'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                              {application.expectedSalary ? `$${application.expectedSalary.toLocaleString()}` : 'Not specified'}
                            </td>
                          </tr>

                          {/* Expanded Interview & Assessment Panel */}
                          {expandedApplicationId === application.id && (
                            <tr>
                              <td colSpan={7} className="px-6 py-4 bg-gray-50 dark:bg-gray-900/50">
                                {interviewsLoading[application.id] ? (
                                  <div className="text-center py-6">
                                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600 mx-auto"></div>
                                    <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">Loading interviews...</p>
                                  </div>
                                ) : !interviewsMap[application.id] || interviewsMap[application.id].length === 0 ? (
                                  <div className="text-center py-6">
                                    <UserIcon className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                                    <p className="text-sm text-gray-500 dark:text-gray-400">No interviews scheduled for this application</p>
                                  </div>
                                ) : (
                                  <div className="space-y-4">
                                    <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                                      Interviews ({interviewsMap[application.id].length})
                                    </h3>
                                    {interviewsMap[application.id].map((interview) => {
                                      const overallScore = getOverallScore(interview.responses);
                                      return (
                                        <div
                                          key={interview.id}
                                          className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4"
                                        >
                                          {/* Interview Header */}
                                          <div className="flex items-center justify-between mb-3">
                                            <div className="flex items-center space-x-3">
                                              <div>
                                                <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                                  {interview.skeletonName}
                                                </p>
                                                <p className="text-xs text-gray-500 dark:text-gray-400">
                                                  Interviewer: {interview.interviewerName}
                                                </p>
                                              </div>
                                            </div>
                                            <div className="flex items-center space-x-3">
                                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getInterviewStatusColor(interview.status)}`}>
                                                {interview.status.replace('_', ' ')}
                                              </span>
                                              {interview.status === InterviewStatus.COMPLETED && interview.responses.length > 0 && (
                                                <div className="flex items-center space-x-1">
                                                  <StarIcon className={`h-4 w-4 ${getRatingColor(overallScore)}`} />
                                                  <span className={`text-sm font-bold ${getRatingColor(overallScore)}`}>
                                                    {overallScore}/100
                                                  </span>
                                                </div>
                                              )}
                                            </div>
                                          </div>

                                          {/* Interview Meta */}
                                          <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500 dark:text-gray-400 mb-3">
                                            {interview.scheduledAt && (
                                              <span>Scheduled: {formatDate(interview.scheduledAt)}</span>
                                            )}
                                            {interview.completedAt && (
                                              <span>Completed: {formatDate(interview.completedAt)}</span>
                                            )}
                                            <span>Assigned by: {interview.assignedByName}</span>
                                          </div>

                                          {/* Assessment Scores - only for completed interviews */}
                                          {interview.status === InterviewStatus.COMPLETED && interview.responses.length > 0 && (
                                            <div className="mt-3 border-t border-gray-200 dark:border-gray-700 pt-3">
                                              <h4 className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider mb-2">
                                                Assessment Scores
                                              </h4>
                                              <div className="space-y-2">
                                                {interview.responses.map((response, idx) => (
                                                  <div key={idx} className="bg-gray-50 dark:bg-gray-900/50 rounded-md p-3">
                                                    <div className="flex items-center justify-between mb-1">
                                                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                                        {response.title}
                                                      </span>
                                                      <span className={`text-sm font-bold ${getRatingColor(response.rating)}`}>
                                                        {response.rating}/100
                                                      </span>
                                                    </div>
                                                    {/* Rating bar */}
                                                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5 mb-2">
                                                      <div
                                                        className={`h-1.5 rounded-full ${getRatingBarColor(response.rating)}`}
                                                        style={{ width: `${response.rating}%` }}
                                                      ></div>
                                                    </div>
                                                    {response.feedback && (
                                                      <p className="text-xs text-gray-600 dark:text-gray-400 italic">
                                                        "{response.feedback}"
                                                      </p>
                                                    )}
                                                  </div>
                                                ))}
                                              </div>
                                            </div>
                                          )}

                                          {/* Pending interview info */}
                                          {interview.status !== InterviewStatus.COMPLETED && (
                                            <div className="mt-2 text-xs text-gray-500 dark:text-gray-400 italic">
                                              Assessment scores will be available once the interview is completed.
                                            </div>
                                          )}
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* User Details Tab */}
        {activeTab === 'details' && (
          <UserDetailsContent 
            userId={userId || null}
            token={token!}
            onUserUpdated={fetchUserProfile}
          />
        )}
      </div>
    </div>
  );
};

export default AdminUserProfilePage;